import {AxiosError, AxiosResponse} from "axios";
import {useState} from "react";
import Button from 'react-bootstrap/Button';
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from 'react-bootstrap/Form';
import Modal from "react-bootstrap/Modal";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

export interface ChangePasswordParam {
    currentPassword: string;
    newPassword: string;
    newPasswordRepeat: string;
}

interface ChangePasswordProps {
    onClose : OnClose
    changePasswordFunc : (param : ChangePasswordParam) => Promise<AxiosResponse<void, ChangePasswordParam>>
}

export const ChangePassword = ({onClose, changePasswordFunc} : ChangePasswordProps) =>
{
    /**
     * Navigate is used to navigate to the main page after signup
     */
    const navigate = useNavigate();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The current password for the user
     */
    const [currentPassword, setCurrentPassword] = useState<string>("");
    /**
     * The new password for the user
     */
    const [newPassword, setNewPassword] = useState<string>("");
    /**
     * The new repeated password for the user
     */
    const [newPasswordRepeat, setNewPasswordRepeat] = useState<string>("");
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    const changePassword = () =>
    {
        if (newPassword === undefined) {
            throw Error("newPassword is undefined");
        }

        if (newPassword !== newPasswordRepeat)
        {
            setErrorMessage(t('change-password.password-mismatch'));

            return;
        }

        changePasswordFunc({currentPassword, newPassword, newPasswordRepeat})
            .then(() =>
            {
                onClose(DialogCloseCause.Succes)
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error changing password", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
    }

    return (
        <Modal show={true} centered={true} animation={false} onHide={() => onClose(DialogCloseCause.Cancel)} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t('change-password.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                {/* show an alert if a validation error occurred */}
                <RenderAlert message={errorMessage}/>

                <FloatingLabel label={t('change-password.current-password-label')} className="text-secondary mb-3">
                    <Form.Control type="password" value={currentPassword} placeholder="Current Password"
                                  onChange={e => setCurrentPassword(e.target.value)}/>
                </FloatingLabel>

                <FloatingLabel label={t('change-password.new-password-label')} className="text-secondary mb-3">
                    <Form.Control type="password" value={newPassword} placeholder="New Password"
                                  onChange={e => setNewPassword(e.target.value)}/>
                </FloatingLabel>

                <FloatingLabel label={t('change-password.new-password-repeat-label')} className="text-secondary mb-3">
                    <Form.Control type="password" value={newPasswordRepeat} placeholder="New Password Repeat"
                                  onChange={e => setNewPasswordRepeat(e.target.value)}/>
                </FloatingLabel>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" disabled={StringUtils.isEmpty(currentPassword) || StringUtils.isEmpty(newPassword) || StringUtils.isEmpty(newPasswordRepeat)}
                        onClick={changePassword}>
                    {t('change-password.change-password-button')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
